import { Rule } from '../types';
import { v4 as uuidv4 } from 'uuid';

export const sampleRules: Rule[] = [
  {
    id: uuidv4(),
    name: 'Heat Warning for Brachycephalic Breeds',
    description: 'Alert for flat-faced breeds when temperature is high',
    filters: {
      combinator: 'or',
      rules: [
        { field: 'breed', operator: '=', value: 'french_bulldog' },
        { field: 'breed', operator: '=', value: 'bulldog' },
        { field: 'breed', operator: '=', value: 'pug' },
        { field: 'breed', operator: '=', value: 'boston_terrier' }
      ]
    },
    triggers: {
      combinator: 'and',
      rules: [
        { field: 'temperature', operator: '>', value: '25' },
        { field: 'humidity', operator: '>', value: '60' }
      ]
    },
    notification: {
      id: uuidv4(),
      topic: 'Uwaga na upał! 🌡️',
      body: 'Twój pies łatwo się przegrzewa. Unikaj spacerów w południe, zapewnij dostęp do wody i chłodne miejsce do odpoczynku.',
      priority: 'high',
      channels: ['push', 'email']
    },
    active: true,
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-15')
  },
  {
    id: uuidv4(),
    name: 'Senior Dog Arthritis Alert',
    description: 'Weather-based alert for senior dogs with arthritis',
    filters: {
      combinator: 'and',
      rules: [
        { field: 'ageCategory', operator: '=', value: 'senior' },
        { field: 'healthConditions', operator: '=', value: 'arthritis' }
      ]
    },
    triggers: {
      combinator: 'or',
      rules: [
        { field: 'weather', operator: '=', value: 'rainy' },
        { field: 'temperature', operator: '<', value: '10' },
        { field: 'humidity', operator: '>', value: '80' }
      ]
    },
    notification: {
      id: uuidv4(),
      topic: 'Pogoda a zdrowie stawów',
      body: 'Zimna i wilgotna pogoda może nasilać bóle stawów. Rozważ krótsze spacery i zapewnij ciepłe legowisko.',
      priority: 'medium',
      channels: ['push']
    },
    active: true,
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-01-20')
  },
  {
    id: uuidv4(),
    name: 'Puppy Vaccination Reminder',
    description: 'Reminder for puppy vaccinations',
    filters: {
      combinator: 'and',
      rules: [
        { field: 'ageCategory', operator: '=', value: 'puppy' }
      ]
    },
    triggers: {
      combinator: 'and',
      rules: [
        { field: 'vaccinationDue', operator: '<=', value: '7' },
        { field: 'vaccinationDue', operator: '>', value: '0' }
      ]
    },
    notification: {
      id: uuidv4(),
      topic: 'Przypomnienie o szczepieniu 💉',
      body: 'Zbliża się termin szczepienia Twojego szczeniaka. Umów wizytę u weterynarza.',
      priority: 'high',
      channels: ['push', 'email', 'sms']
    },
    active: true,
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-02-01')
  },
  {
    id: uuidv4(),
    name: 'High Activity Dog Exercise',
    description: 'Exercise reminder for high-energy breeds',
    filters: {
      combinator: 'or',
      rules: [
        { field: 'breed', operator: '=', value: 'border_collie' },
        { field: 'breed', operator: '=', value: 'german_shepherd' },
        { field: 'breed', operator: '=', value: 'siberian_husky' },
        { field: 'breed', operator: '=', value: 'labrador_retriever' }
      ]
    },
    triggers: {
      combinator: 'and',
      rules: [
        { field: 'dayOfWeek', operator: 'in', value: [1, 2, 3, 4, 5] },
        { field: 'timeOfDay', operator: '=', value: 'morning' },
        { field: 'dailySteps', operator: '<', value: '5000' }
      ]
    },
    notification: {
      id: uuidv4(),
      topic: 'Czas na aktywność! 🏃',
      body: 'Twój pies potrzebuje regularnej aktywności. Zaplanuj dłuższy spacer lub zabawę.',
      priority: 'low',
      channels: ['push']
    },
    active: true,
    createdAt: new Date('2024-02-10'),
    updatedAt: new Date('2024-02-10')
  },
  {
    id: uuidv4(),
    name: 'Weight Management Alert',
    description: 'Alert for dogs with weight issues',
    filters: {
      combinator: 'or',
      rules: [
        { field: 'healthConditions', operator: '=', value: 'obesity' },
        {
          combinator: 'and',
          rules: [
            { field: 'weight', operator: '>', value: '30' },
            { field: 'size', operator: '=', value: 'medium' }
          ]
        }
      ]
    },
    triggers: {
      combinator: 'and',
      rules: [
        { field: 'weightChange', operator: '>', value: '5' },
        { field: 'frequency', operator: '=', value: 'monthly' }
      ]
    },
    notification: {
      id: uuidv4(),
      topic: 'Kontrola wagi 📊',
      body: 'Zaobserwowano zmianę wagi Twojego psa. Skonsultuj dietę z weterynarzem.',
      priority: 'medium',
      channels: ['push', 'email']
    },
    active: false,
    createdAt: new Date('2024-02-15'),
    updatedAt: new Date('2024-02-15')
  },
  // Cat-specific rules
  {
    id: uuidv4(),
    name: 'Indoor Cat Exercise Reminder',
    description: 'Daily exercise reminder for indoor cats',
    filters: {
      combinator: 'and',
      rules: [
        { field: 'petType', operator: '=', value: 'cat' },
        { field: 'ageCategory', operator: '!=', value: 'senior' }
      ]
    },
    triggers: {
      combinator: 'and',
      rules: [
        { field: 'timeOfDay', operator: '=', value: 'evening' },
        { field: 'dailyActivity', operator: '<', value: '30' }
      ]
    },
    notification: {
      id: uuidv4(),
      topic: 'Playtime for Your Cat! 🐱',
      body: 'Your cat needs daily interactive play. Try laser pointers, feather wands, or puzzle feeders for 15-20 minutes.',
      priority: 'low',
      channels: ['push']
    },
    active: true,
    createdAt: new Date('2024-03-01'),
    updatedAt: new Date('2024-03-01')
  },
  {
    id: uuidv4(),
    name: 'Cat Hairball Prevention',
    description: 'Grooming reminder for long-haired cat breeds',
    filters: {
      combinator: 'and',
      rules: [
        { field: 'petType', operator: '=', value: 'cat' },
        {
          combinator: 'or',
          rules: [
            { field: 'breed', operator: '=', value: 'cat_persian' },
            { field: 'breed', operator: '=', value: 'cat_maine_coon' },
            { field: 'breed', operator: '=', value: 'cat_norwegian_forest_cat' },
            { field: 'breed', operator: '=', value: 'cat_ragdoll' }
          ]
        }
      ]
    },
    triggers: {
      combinator: 'and',
      rules: [
        { field: 'dayOfWeek', operator: 'in', value: [1, 3, 5] },
        { field: 'timeOfDay', operator: '=', value: 'morning' }
      ]
    },
    notification: {
      id: uuidv4(),
      topic: 'Grooming Time 🧹',
      body: 'Long-haired cats need regular brushing to prevent hairballs. Spend 5-10 minutes grooming your cat today.',
      priority: 'medium',
      channels: ['push']
    },
    active: true,
    createdAt: new Date('2024-03-05'),
    updatedAt: new Date('2024-03-05')
  },
  {
    id: uuidv4(),
    name: 'Senior Cat Health Check',
    description: 'Health monitoring for senior cats',
    filters: {
      combinator: 'and',
      rules: [
        { field: 'petType', operator: '=', value: 'cat' },
        { field: 'ageCategory', operator: '=', value: 'senior' }
      ]
    },
    triggers: {
      combinator: 'and',
      rules: [
        { field: 'vetCheckDue', operator: '<=', value: '30' },
        { field: 'vetCheckDue', operator: '>', value: '0' }
      ]
    },
    notification: {
      id: uuidv4(),
      topic: 'Senior Cat Health Check 🏥',
      body: 'Senior cats need regular health checkups. Schedule a vet appointment to monitor kidney function, dental health, and weight.',
      priority: 'high',
      channels: ['push', 'email']
    },
    active: true,
    createdAt: new Date('2024-03-10'),
    updatedAt: new Date('2024-03-10')
  }
];